import {css, StyleSheet} from 'aphrodite';
import React, {useState} from 'react';

import ModelTypeConstants from 'constants/ModelTypeConstants';
import {Filter, FilterKey, keyToEmptyFilter, SortKey} from 'search/SearchConstants';
import {ButtonStyle} from 'ui-library/buttons/Button';
import * as Colors from 'ui-library/Colors';
import * as RichObjectTable from 'ui-library/RichObjectTable';
import {TableCollapserContext} from 'ui-library/tables/TableCollapserContext';

import {RenderTaskStatus, RenderWorksheetChip} from '../shared/ProcedureRunTableUtil';
import StudyItemsTabButton from '../utils/ui/StudyItemsTabButton';
import {getProcedureRunParamsTableColumnsAndHeaderCells} from './ProcedureRunParamsTableUtil';
import {PLANNED, ProcedureRunOverview, UnitOperationStatusType} from './useProcessDevelopmentStudyProgress';

function getAdditionalFilters(overview: ProcedureRunOverview): Array<Filter> {
  const additionalFilters: Array<Filter> = [
    {
      ...keyToEmptyFilter(FilterKey.TYPES),
      value: [ModelTypeConstants.ASSAY_UNIT_OPERATION_TASK],
    },
  ];
  const rootTaskIds = new Set(
    overview.conditions.flatMap((condition) =>
      condition.replicates
        .map((replicate) => replicate.processRootTaskId)
        .filter((taskId): taskId is string => taskId != null)
    )
  );
  if (rootTaskIds.size > 0) {
    additionalFilters.push({
      ...keyToEmptyFilter(FilterKey.ASSAY_CONDITION_REPLICATE_RUN_TASKS),
      value: [...rootTaskIds],
    });
  }
  return additionalFilters;
}

export function ProcedureRunParamsTable({
  studyId,
  overview,
  isMultiExperimentEnabled,
  enabledStatusFilters,
}: {
  studyId?: string; // Only needed for the multi-experiment view
  overview: ProcedureRunOverview;
  isMultiExperimentEnabled: boolean;
  enabledStatusFilters?: Map<UnitOperationStatusType, boolean>; // Only needed for the single-experiment view
}): JSX.Element {
  const [isTableCollapsed, setIsTableCollapsed] = useState(!isMultiExperimentEnabled);
  const toggleTableCollapsed = (): void => {
    setIsTableCollapsed(!isTableCollapsed);
  };

  const areFiltersEnabled =
    enabledStatusFilters && Array.from(enabledStatusFilters.values()).filter((value) => value).length > 0;
  const filteredUnitOperations = overview.unitOperationExecutionInstances.filter(
    (unitOperationExecutionInstance) => {
      const unitOperationStatus = unitOperationExecutionInstance?.status?.statusType ?? PLANNED;
      return !areFiltersEnabled || enabledStatusFilters.get(unitOperationStatus);
    }
  );
  const showUnitOpsColumn = !isMultiExperimentEnabled || overview.unitOperationMethods.length > 1;
  const [columns, headerCells] = getProcedureRunParamsTableColumnsAndHeaderCells(
    isMultiExperimentEnabled,
    showUnitOpsColumn
  );
  let currentUnitOperationId: string | null = null;
  let currentConditionId: string | null = null;
  const numRuns = overview.conditions.reduce(
    (accumulator, condition) => accumulator + condition.replicates.length,
    0
  );
  return (
    <>
      {/* eslint-disable-next-line react/jsx-no-constructed-context-values -- Disabling for now, but worth fixing */}
      <TableCollapserContext.Provider value={{isTableInitiallyCollapsed: isTableCollapsed}}>
        <RichObjectTable.Table
          dataTestAttrs={{component: 'ProcedureRunParamsTable', element: 'root'}}
          showCollapsedMessage={false}
          isCollapsible={true}
          isCollapsed={isTableCollapsed}
          onToggleCollapsed={toggleTableCollapsed}
          columns={columns}
          headerCells={headerCells}
          headerColor={isMultiExperimentEnabled ? Colors.WHITE : Colors.GRAY_LIGHT}
          largerHeader={true}
          leftHeader={
            !isMultiExperimentEnabled ? (
              <RenderLeftHeader label="Unit operations" toggleTableCollapsed={toggleTableCollapsed} />
            ) : (
              <div className={css(styles.runsHeaderText)}>Runs</div>
            )
          }
          rightHeader={
            isMultiExperimentEnabled ? (
              <>
                {studyId !== undefined ? (
                  <div className={css(styles.runDetailsLink)}>
                    <StudyItemsTabButton
                      studyId={studyId}
                      style={ButtonStyle.TEXT}
                      additionalFilters={getAdditionalFilters(overview)}
                      sorts={[{key: SortKey.SORT_ASSAY_METHOD_ASSAY_ORDER, reverse: false}]}
                      text="See detailed view"
                    />
                  </div>
                ) : null}
                <div className={css(styles.runsHeaderText)}>Total Runs: {`${numRuns}`}</div>
              </>
            ) : undefined
          }
          collapsedHeaderText={`Total Runs: ${numRuns}`}
        >
          {overview.unitOperationMethods.map((unitOperation) =>
            overview.conditions.map((condition) =>
              condition.replicates.map((replicate) => {
                const unitOperationExecutionInstance = filteredUnitOperations.find(
                  (instance) =>
                    instance.methodId === unitOperation.id &&
                    instance.conditionId === condition.id &&
                    instance.replicateNumber === replicate.replicateNumber
                );
                if (unitOperationExecutionInstance) {
                  let methodLength = 0;
                  let conditionLength = 0;

                  // We only want to render the method and condition cells once per method and condition,
                  // therefore we need to keep track of the current method and condition IDs
                  const isMethodCellRendered = currentUnitOperationId !== unitOperation.id;
                  if (isMethodCellRendered) {
                    currentUnitOperationId = unitOperation.id;
                    currentConditionId = null;
                    methodLength = filteredUnitOperations.filter(
                      (instance) => instance.methodId === unitOperation.id
                    ).length;
                  }
                  const isConditionCellRendered = currentConditionId !== condition.id;
                  if (isConditionCellRendered) {
                    currentConditionId = condition.id;
                    conditionLength = filteredUnitOperations.filter(
                      (instance) =>
                        instance.methodId === unitOperation.id && instance.conditionId === condition.id
                    ).length;
                  }
                  // used to allow name and description columns to span all the replicates
                  const isFirstReplicate = replicate.replicateNumber === 1;

                  return (
                    <RichObjectTable.Row
                      key={unitOperationExecutionInstance.id}
                      className={css(styles.unitOperationRow)}
                    >
                      {showUnitOpsColumn ? (
                        <>
                          {!isMethodCellRendered ? null : (
                            <RichObjectTable.Cell
                              rowSpan={methodLength}
                              className={css(styles.cellWithRowSpan)}
                            >
                              <span>{unitOperation.name}</span>
                            </RichObjectTable.Cell>
                          )}
                          {!isConditionCellRendered ? null : (
                            <RichObjectTable.Cell
                              rowSpan={conditionLength}
                              className={css(styles.cellWithRowSpan)}
                            >
                              <span>{condition.name}</span>
                            </RichObjectTable.Cell>
                          )}
                        </>
                      ) : !isFirstReplicate ? null : (
                        <RichObjectTable.Cell
                          rowSpan={conditionLength}
                          className={css(styles.cellWithRowSpan)}
                        >
                          <span>{condition.name}</span>
                        </RichObjectTable.Cell>
                      )}
                      {!isFirstReplicate ? null : (
                        <RichObjectTable.Cell
                          rowSpan={conditionLength}
                          className={css(styles.cellWithRowSpan)}
                        >
                          <span>{`${condition.description}`}</span>
                        </RichObjectTable.Cell>
                      )}
                      <RichObjectTable.Cell>
                        <span>{replicate.replicateNumber}</span>
                      </RichObjectTable.Cell>
                      {isMultiExperimentEnabled && (
                        <RichObjectTable.Cell>
                          <span>{unitOperationExecutionInstance.worksheetCreatedAt}</span>
                        </RichObjectTable.Cell>
                      )}
                      <RichObjectTable.Cell>
                        <RenderTaskStatus
                          statusType={unitOperationExecutionInstance.status?.statusType ?? null}
                        />
                      </RichObjectTable.Cell>
                      <RichObjectTable.Cell>
                        <RenderWorksheetChip worksheetId={unitOperationExecutionInstance.worksheetId} />
                      </RichObjectTable.Cell>
                    </RichObjectTable.Row>
                  );
                } else {
                  return null;
                }
              })
            )
          )}
        </RichObjectTable.Table>
      </TableCollapserContext.Provider>
    </>
  );
}

/**
 * @package exported for testing
 */
export function RenderLeftHeader({
  label,
  toggleTableCollapsed,
}: {
  label: string;
  toggleTableCollapsed: () => void;
}): JSX.Element {
  return (
    <div
      data-test-component="ProcessDevelopmentStudyExecutionProgress"
      data-test-element="unitOperationTableHeader"
      onClick={toggleTableCollapsed}
    >
      <span className={css(styles.leftHeaderText)}>{`${label}`}</span>
    </div>
  );
}

const styles = StyleSheet.create({
  leftHeaderText: {
    fontSize: 'larger',
  },
  leftHeader: {
    paddingTop: 10,
    paddingBottom: 10,
    cursor: 'pointer',
  },
  runDetailsLink: {
    display: 'inline-block',
  },
  runsHeaderText: {
    fontSize: 13,
    display: 'inline-block',
    verticalAlign: 'middle',
  },
  headerText: {
    minHeight: 30,
    paddingTop: 8,
    paddingLeft: 8,
    marginTop: -5,
    marginBottom: -5,
    marginLeft: -8,
    marginRight: -8,
    backgroundColor: Colors.GRAY_LIGHTER,
  },
  // Row Styles
  unitOperationRow: {
    minHeight: 40,
  },
  cellWithRowSpan: {
    verticalAlign: 'top',
  },
});
